import { CustomAttributes } from "../../pojo/CustomAttributes";
import type { MaybeNil } from "../../pojo/Util";
import { S25Util } from "../../util/s25-util";
import { AnswerType, IngestionFieldDefinition } from "./data.ingestion.service";
import { Workflow } from "../workflow/service/Workflow";

/** Workflow input tokens reference ingestion field definition ids with this prefix. */
export const WORKFLOW_FIELD_REFERENCE_PREFIX = /^\$CNET-(\d+)-/;

/** WS payloads use {@link MaybeNil} for some strings; coerce to a plain string for workflow hydration. */
export function stringFromMaybeNil(value: MaybeNil<string> | undefined | null): string {
    if (value == null) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    return "";
}

export function parseFieldDefinitionIdFromWorkflowToken(token: string | undefined): number | undefined {
    if (!token?.trim()) {
        return undefined;
    }
    const match = WORKFLOW_FIELD_REFERENCE_PREFIX.exec(token.trim());
    if (!match) {
        return undefined;
    }
    const fieldDefinitionId = parseInt(match[1], 10);
    return Number.isNaN(fieldDefinitionId) ? undefined : fieldDefinitionId;
}

/**
 * Finds the best-matching {@link Workflow.WorkflowAction} for a workflow item, using
 * {@link prioritizedActionCodes} order (index 0 wins on sight; among other listed codes,
 * a better rank replaces a worse one, and equal rank keeps the last seen match).
 */
export function findPrioritizedWorkflowSystemActionForItem(
    workflowProcess: Workflow.WorkflowProcess | undefined,
    actionItemTypeId: number,
    actionItemId: number,
    prioritizedActionCodes: readonly string[],
): Workflow.WorkflowAction | undefined {
    if (!workflowProcess?.stages?.length || !prioritizedActionCodes.length) {
        return undefined;
    }
    const codeRank = new Map(prioritizedActionCodes.map((code, index) => [code, index]));
    let fallbackAction: Workflow.WorkflowAction | undefined;
    let fallbackRank = Number.POSITIVE_INFINITY;

    for (const stage of workflowProcess.stages) {
        for (const action of stage.actions ?? []) {
            if (action.actionType !== "SYSTEM_ACTION") {
                continue;
            }
            if (action.actionItemTypeId !== actionItemTypeId || Number(action.actionItemId) !== Number(actionItemId)) {
                continue;
            }
            const actionCode = action.actionCode;
            if (!actionCode) {
                continue;
            }
            const rank = codeRank.get(actionCode);
            if (rank === undefined) {
                continue;
            }
            if (rank === 0) {
                return action;
            }
            if (rank <= fallbackRank) {
                fallbackRank = rank;
                fallbackAction = action;
            }
        }
    }
    return fallbackAction;
}

/**
 * Fills {@link IngestionFieldDefinition} answers from a custom-attribute row when the definition's
 * `fieldType` is listed in `fieldTypes` and {@link IngestionFieldDefinition.custAtrbId} matches a row's attribute id.
 */
export function hydrateIngestionFieldFromCustomAttributeRows(
    fieldDef: IngestionFieldDefinition,
    attributeRows: CustomAttributes.DataI[],
    fieldTypes: readonly string[],
): boolean {
    if (!fieldDef.fieldType || !fieldTypes.includes(fieldDef.fieldType) || fieldDef.custAtrbId == null) {
        return false;
    }
    const row = attributeRows.find((attr) => Number(attr.attribute_id) === Number(fieldDef.custAtrbId));
    if (!row) {
        return false;
    }
    const dataType = String(row.attribute_type ?? fieldDef.custAtrb?.atrbDataType ?? "");
    const rawValue = row.attribute_value != null ? String(row.attribute_value) : "";
    const multiVal = S25Util.toBool(row.multi_val) || fieldDef.custAtrb?.multiVal === true;

    if (multiVal && dataType === CustomAttributes.type.Text) {
        try {
            const parsed = JSON.parse(rawValue) as { item?: string }[];
            const options = S25Util.array.forceArray(parsed).map((entry) => ({
                option: String(entry.item ?? "").trim(),
            }));
            fieldDef.answers = options.filter((option) => !!option.option);
            fieldDef.longAnswer = "";
        } catch {
            fieldDef.answers = [];
            fieldDef.longAnswer = "";
        }
        return true;
    }

    fieldDef.answerType = AnswerType.fromAtrbDataType(dataType) ?? fieldDef.answerType;
    if (dataType === CustomAttributes.type.Boolean) {
        fieldDef.longAnswer = S25Util.toBool(rawValue) ? "true" : "false";
    } else {
        fieldDef.longAnswer = rawValue;
    }
    return true;
}
